import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Invoice } from '../../../../interfaces/stripe/invoice.interface';
import { SpinnerService } from '../../../../services/spinner/spinner.service';
import { StripeService } from '../../../../services/stripe/stripe.service';

type RefundMode = 'full' | 'partial';

interface RefundDialogData {
  invoice: Invoice;
}

interface RefundDialogResult {
  amount: number;
  reasonDescription: string;
}

interface RefundDetailsDialogData {
  invoice: Invoice;
}

@Component({
  selector: 'app-superadmin-users-stripe-refund-dialog',
  template: `
    <h2 mat-dialog-title>Issue Refund</h2>

    <mat-dialog-content class="stripe-refund-dialog" [formGroup]="refundForm">
      <div class="refund-summary">
        <strong>{{data.invoice.info}}</strong>
        <span>Remaining: {{refundableAmount | currency}}</span>
      </div>

      <mat-radio-group formControlName="mode" class="refund-mode">
        <mat-radio-button value="full">Full refund</mat-radio-button>
        <mat-radio-button value="partial">Partial refund</mat-radio-button>
      </mat-radio-group>

      <mat-form-field appearance="outline" *ngIf="refundForm.get('mode')?.value === 'partial'">
        <mat-label>Refund amount</mat-label>
        <input matInput type="number" min="0.01" [max]="refundableAmount" step="0.01" formControlName="amount">
        <span matPrefix>$</span>
        <mat-error>Enter an amount up to the remaining refundable amount.</mat-error>
      </mat-form-field>

      <mat-form-field appearance="outline" class="reason-field">
        <mat-label>Reason comment</mat-label>
        <textarea matInput rows="3" formControlName="reasonDescription"></textarea>
        <mat-error>Reason comment is required.</mat-error>
      </mat-form-field>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="cancel()">Cancel</button>
      <button mat-raised-button color="primary" type="button" (click)="submit()">Issue Refund</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .stripe-refund-dialog {
      display: flex;
      flex-direction: column;
      gap: 16px;
      min-width: 420px;
    }

    .refund-summary {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .refund-summary span {
      color: #5f6b7a;
      font-size: 13px;
    }

    .refund-mode {
      display: flex;
      gap: 20px;
    }

    .reason-field {
      width: 100%;
    }
  `],
})
export class SuperadminUsersStripeRefundDialogComponent implements OnInit {
  public readonly refundableAmount = Number(this.data.invoice.refundableAmount || 0);
  public refundForm = this.formBuilder.group({
    mode: ['full' as RefundMode, Validators.required],
    amount: [null],
    reasonDescription: ['', Validators.required],
  });

  constructor(
    private dialogRef: MatDialogRef<SuperadminUsersStripeRefundDialogComponent, RefundDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: RefundDialogData,
    private formBuilder: UntypedFormBuilder,
  ) {}

  ngOnInit(): void {
    this.refundForm.get('mode')?.valueChanges.subscribe((mode: RefundMode) => {
      const amountControl = this.refundForm.get('amount');
      if (mode === 'partial') {
        amountControl?.setValidators([
          Validators.required,
          Validators.min(0.01),
          Validators.max(this.refundableAmount),
        ]);
      } else {
        amountControl?.clearValidators();
        amountControl?.setValue(null);
      }
      amountControl?.updateValueAndValidity();
    });
  }

  submit(): void {
    if (this.refundForm.invalid) {
      this.refundForm.markAllAsTouched();
      return;
    }

    const requestedAmount = this.refundForm.get('mode')?.value === 'partial'
      ? Number(this.refundForm.get('amount')?.value)
      : this.refundableAmount;
    const reasonDescription = `${this.refundForm.get('reasonDescription')?.value || ''}`.trim();

    if (!Number.isFinite(requestedAmount) || requestedAmount <= 0 || requestedAmount > this.refundableAmount) {
      this.refundForm.get('amount')?.setErrors({ amount: true });
      return;
    }

    if (!reasonDescription) {
      this.refundForm.get('reasonDescription')?.setErrors({ required: true });
      return;
    }

    this.dialogRef.close({
      amount: requestedAmount,
      reasonDescription,
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}

@Component({
  selector: 'app-superadmin-users-stripe-refund-details-dialog',
  template: `
    <h2 mat-dialog-title>Refund Details</h2>

    <mat-dialog-content class="stripe-refund-details-dialog">
      <div class="refund-details-summary">
        <strong>{{data.invoice.info}}</strong>
        <span>{{data.invoice.date | date:'medium'}}</span>
      </div>

      <div class="refund-detail-grid">
        <div>
          <span>Status</span>
          <strong>{{data.invoice.status}}</strong>
        </div>
        <div>
          <span>Payment amount</span>
          <strong>{{data.invoice.amount | currency}}</strong>
        </div>
        <div>
          <span>Refunded amount</span>
          <strong>{{(data.invoice.amountRefunded || 0) | currency}}</strong>
        </div>
        <div *ngIf="data.invoice.dateRefunded">
          <span>Refund date</span>
          <strong>{{data.invoice.dateRefunded | date:'medium'}}</strong>
        </div>
      </div>

      <div class="refund-records" *ngIf="data.invoice.refunds?.length; else noRefundDetails">
        <div class="refund-record" *ngFor="let refund of data.invoice.refunds">
          <div class="refund-record-header">
            <strong>{{refund.amount | currency}}</strong>
            <span>{{refund.status || 'Refunded'}}</span>
          </div>
          <div class="refund-record-meta" *ngIf="refund.created">{{refund.created | date:'medium'}}</div>
          <div class="refund-comment" *ngIf="refund.reasonDescription">
            {{refund.reasonDescription}}
          </div>
          <div class="refund-record-id" *ngIf="refund.id">{{refund.id}}</div>
        </div>
      </div>

      <ng-template #noRefundDetails>
        <div class="empty-refund-details">No refund detail records were returned.</div>
      </ng-template>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="close()">Close</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .stripe-refund-details-dialog {
      display: flex;
      flex-direction: column;
      gap: 16px;
      min-width: 480px;
    }

    .refund-details-summary {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .refund-details-summary span,
    .refund-detail-grid span,
    .refund-record-meta,
    .refund-record-id,
    .empty-refund-details {
      color: #5f6b7a;
      font-size: 13px;
    }

    .refund-detail-grid {
      display: grid;
      gap: 12px;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .refund-detail-grid div {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .refund-records {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .refund-record {
      border: 1px solid #d8dee8;
      border-radius: 4px;
      padding: 12px;
    }

    .refund-record-header {
      align-items: center;
      display: flex;
      justify-content: space-between;
      gap: 12px;
    }

    .refund-record-header span {
      color: #2f6f4e;
      font-size: 13px;
      font-weight: 600;
    }

    .refund-comment {
      margin-top: 8px;
      white-space: pre-wrap;
    }

    .refund-record-id {
      margin-top: 8px;
      word-break: break-all;
    }
  `],
})
export class SuperadminUsersStripeRefundDetailsDialogComponent {
  constructor(
    private dialogRef: MatDialogRef<SuperadminUsersStripeRefundDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RefundDetailsDialogData,
  ) {}

  close(): void {
    this.dialogRef.close();
  }
}

@Component({
  selector: 'app-superadmin-users-stripe-payments-dialog',
  templateUrl: './superadmin-users-stripe-payments-dialog.component.html',
  styleUrls: ['./superadmin-users-stripe-payments-dialog.component.scss'],
})
export class SuperadminUsersStripePaymentsDialogComponent implements OnInit {
  public customerId = '';
  public invoices: Invoice[] = [];
  public loading = false;
  public displayedColumns = ['date', 'info', 'paymentType', 'amount', 'refunded', 'remaining', 'status', 'actions'];

  constructor(
    private dialogRef: MatDialogRef<SuperadminUsersStripePaymentsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { user: any },
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private spinnerService: SpinnerService,
    private stripeService: StripeService,
  ) {}

  ngOnInit(): void {
    this.loadInvoices();
  }

  async loadInvoices(): Promise<void> {
    this.loading = true;
    this.spinnerService.show('Loading Stripe payments...');
    try {
      const paymentInfo = await this.stripeService.getPaymentInfo(this.data.user.id);
      this.customerId = paymentInfo?.stripeCustomerId || '';
      if (!this.customerId) {
        throw new Error('User does not have a Stripe customer ID.');
      }
      const invoices = await this.stripeService.getInvoicesForCustomer(this.customerId, true);
      this.invoices = (invoices || [])
        .filter((invoice: Invoice) => invoice.amount > 0)
        .map((invoice: Invoice) => ({
          ...invoice,
          date: new Date((invoice.date as number) * 1000),
          dateRefunded: invoice.dateRefunded ? new Date((invoice.dateRefunded as number) * 1000) : null,
          amountRefunded: Number(invoice.amountRefunded || 0),
          refundableAmount: Number(invoice.refundableAmount || 0),
          refunds: (invoice.refunds || []).map((refund) => ({
            ...refund,
            amount: Number(refund.amount || 0),
            created: refund.created ? new Date((refund.created as number) * 1000) : null,
          })),
        }));
    } catch (error: any) {
      console.error('Error loading Stripe payments', error);
      this.snackBar.open(error?.message || 'Unable to load Stripe payments', 'Close', { duration: 3000 });
    } finally {
      this.loading = false;
      this.spinnerService.hide();
    }
  }

  async selectRefund(invoice: Invoice): Promise<void> {
    const dialogRef = this.dialog.open<
      SuperadminUsersStripeRefundDialogComponent,
      RefundDialogData,
      RefundDialogResult
    >(SuperadminUsersStripeRefundDialogComponent, {
      width: '520px',
      data: { invoice },
      autoFocus: false,
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (!result) {
      return;
    }

    await this.submitRefund(invoice, result);
  }

  async submitRefund(invoice: Invoice, refund: RefundDialogResult): Promise<void> {
    const refundableAmount = Number(invoice.refundableAmount || 0);
    if (!Number.isFinite(refund.amount) || refund.amount <= 0 || refund.amount > refundableAmount) {
      this.snackBar.open('Refund amount must be greater than $0 and no more than the remaining refundable amount.', 'Close', {
        duration: 3000,
      });
      return;
    }

    this.spinnerService.show('Issuing Stripe refund...');
    try {
      await this.stripeService.issueChargeRefund(
        this.customerId,
        invoice.chargeId || '',
        refund.amount,
        refund.reasonDescription,
      );
      this.snackBar.open('Stripe refund issued', 'Close', { duration: 2500 });
      await this.loadInvoices();
    } catch (error: any) {
      console.error('Error issuing Stripe refund', error);
      this.snackBar.open(error?.data?.message || error?.message || 'Unable to issue Stripe refund', 'Close', {
        duration: 4000,
      });
    } finally {
      this.spinnerService.hide();
    }
  }

  canRefund(invoice: Invoice): boolean {
    return !!invoice.chargeId && Number(invoice.refundableAmount || 0) > 0;
  }

  isRefunded(invoice: Invoice): boolean {
    return `${invoice.status || ''}`.trim().toLowerCase() === 'refunded';
  }

  openRefundDetails(invoice: Invoice): void {
    this.dialog.open<SuperadminUsersStripeRefundDetailsDialogComponent, RefundDetailsDialogData>(
      SuperadminUsersStripeRefundDetailsDialogComponent,
      {
        width: '560px',
        data: { invoice },
        autoFocus: false,
      },
    );
  }

  openInvoice(invoice: Invoice): void {
    if (invoice.url) {
      window.open(invoice.url, '_blank');
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}
